import { useState, useEffect, useCallback } from 'react';
import { ApiClient } from '@utils/apiClient';
import {
  AnalyticsFilters,
  AnalyticsResponse,
  CallPerformanceMetrics,
  ConversationAnalytics,
  PerformanceMetrics,
  AgentPerformance,
  CallVolumeTrend,
  PerformanceTrends,
  RecentCall,
  AnalyticsFiltersData,
} from '../types';

export const useAnalytics = () => {
  const [filters, setFilters] = useState<AnalyticsFilters>({
    dateRange: '30d',
    customFromDate: '',
    customToDate: '',
    agent: '',
  });

  const [callPerformance, setCallPerformance] = useState<CallPerformanceMetrics | null>(null);
  const [conversationAnalytics, setConversationAnalytics] = useState<ConversationAnalytics | null>(
    null
  );
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [agentPerformance, setAgentPerformance] = useState<AgentPerformance[]>([]);
  const [callVolumeTrends, setCallVolumeTrends] = useState<CallVolumeTrend[]>([]);
  const [performanceTrends, setPerformanceTrends] = useState<PerformanceTrends | null>(null);
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [filtersData, setFiltersData] = useState<AnalyticsFiltersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateAnalyticsData = useCallback((data: AnalyticsResponse) => {
    setCallPerformance(data.call_performance);
    setConversationAnalytics(data.conversation_analytics);
    setPerformanceMetrics(data.performance_metrics);
    setAgentPerformance(data.agent_performance);
    setCallVolumeTrends(data.call_volume_trends);
    setPerformanceTrends(data.performance_trends);
    setRecentCalls(data.recent_calls);
    setFiltersData(data.filters);
  }, []);

  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const fetchAnalyticsData = useCallback(
    async (url: string, isInitialLoad = false) => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setFilterLoading(true);
        }
        setError(null);

        const response = await ApiClient.get(url);

        if (response.ok) {
          const data: AnalyticsResponse = await response.json();
          updateAnalyticsData(data);
          if (isFirstLoad) {
            setIsFirstLoad(false);
          }
        } else {
          throw new Error(`Failed to fetch analytics data: ${response.status}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
      } finally {
        if (isInitialLoad) {
          setLoading(false);
        } else {
          setFilterLoading(false);
        }
      }
    },
    [updateAnalyticsData, isFirstLoad]
  );

  const buildAnalyticsUrl = useCallback((filters: AnalyticsFilters) => {
    const params = new URLSearchParams();
    if (filters.dateRange !== 'custom') {
      params.append('date_range', filters.dateRange);
    } else {
      if (filters.customFromDate) {
        params.append('start_date', filters.customFromDate);
      }
      if (filters.customToDate) {
        params.append('end_date', filters.customToDate);
      }
    }
    if (filters.agent) {
      params.append('agent', filters.agent);
    }
    return `/organizations/current/analytics/?${params.toString()}`;
  }, []);

  useEffect(() => {
    const url = buildAnalyticsUrl(filters);
    fetchAnalyticsData(url, isFirstLoad);
  }, [filters, buildAnalyticsUrl, fetchAnalyticsData, isFirstLoad]);

  const viewAgentDetails = useCallback((_agentId: string) => {
    // Implementation pending
  }, []);

  const updateUsageChart = useCallback((_metric: string) => {
    // Implementation pending
  }, []);

  return {
    filters,
    setFilters,
    callPerformance,
    conversationAnalytics,
    performanceMetrics,
    agentPerformance,
    callVolumeTrends,
    performanceTrends,
    recentCalls,
    filtersData,
    loading,
    filterLoading,
    error,
    viewAgentDetails,
    updateUsageChart,
  };
};
